import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pie, PieChart } from "recharts";
import colors from "tailwindcss/colors";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierBookingStatBlockQuery } from "@/app/Dashboard/GraphQL/supplierBookingStatBlockQuery";
import { BookingStatus, ComparisonMode } from "@/gql/graphql";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { TrendIndicator } from "../TrendIndicator";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "@tanstack/react-router";
import { getBookingCancelledStatuses } from "@/app/Bookings/Utils/getBookingCancelledStatuses";
import { IllustrationListApp } from "@/assets/Illustrations";
import { useTranslations } from "use-intl";

const integerNumberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

export function BookingStatusPieChartBlock({
  start,
  end,
  comparisonMode,
}: {
  start: string;
  end: string;
  comparisonMode: ComparisonMode;
}) {
  const t = useTranslations();
  const { supplierId } = useGlobalSupplier();

  if (!supplierId) {
    throw new Error("supplier is required");
  }

  const { data } = useSuspenseGqlQuery(supplierBookingStatBlockQuery, {
    start: start,
    end: end,
    supplierId: supplierId,
    comparisonMode,
  });

  const stats = data.supplierBookingStatBlock;

  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.blocks.bookings.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-48 opacity-80">
              <IllustrationListApp />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              {t("dashboard.blocks.bookings.noData")}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {t("dashboard.blocks.bookings.noDataDescription")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const statusMetrics = [
    {
      key: "pending",
      status: t("dashboard.chartLabels.pending"),
      count: stats.pending_count,
      fill: colors.yellow[500],
      search: {
        bookingStatus: [BookingStatus.AwaitingConfirmation],
      } as const,
    },
    {
      key: "confirmed",
      status: t("dashboard.chartLabels.confirmed"),
      count: stats.confirmed_count,
      fill: colors.green[500],
      search: {
        bookingStatus: [BookingStatus.Confirmed],
      } as const,
    },
    {
      key: "completed",
      status: t("dashboard.chartLabels.completed"),
      count: stats.completed_count,
      fill: colors.blue[500],
      search: {
        bookingStatus: [BookingStatus.Completed],
      } as const,
    },
    {
      key: "cancelled",
      status: t("dashboard.chartLabels.cancelled"),
      count: stats.cancelled_count,
      fill: colors.red[500],
      search: {
        bookingStatus: getBookingCancelledStatuses(),
      } as const,
    },
  ];

  const chartData = statusMetrics
    .filter((item) => item.count > 0)
    .map((item) => ({
      status: item.status,
      count: item.count,
      fill: item.fill,
    }));

  const chartConfig = {
    count: {
      label: t("dashboard.blocks.bookings.title"),
    },
  } satisfies ChartConfig;

  const totalBookings = statusMetrics.reduce(
    (sum, metric) => sum + metric.count,
    0,
  );

  const successfulBookings = stats.confirmed_count + stats.completed_count;

  const getPreviousCount = (
    count: number,
    trend: number | null | undefined,
  ) => {
    if (trend === null || trend === undefined) {
      return count;
    }
    const ratio = 1 + trend / 100;
    if (!Number.isFinite(ratio) || ratio === 0) {
      return count;
    }
    return count / ratio;
  };

  const previousSuccessfulBookings =
    stats.successful_bookings_trend !== null &&
    stats.successful_bookings_trend !== undefined
      ? getPreviousCount(successfulBookings, stats.successful_bookings_trend)
      : null;

  const getConfirmationTimePosition = (hours: number | null | undefined) => {
    if (hours === null || hours === undefined) return 0;
    if (hours <= 4) return 0;
    if (hours >= 48) return 100;
    return ((hours - 4) / (48 - 4)) * 100;
  };

  return (
    <Card className="flex flex-col justify-between">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("dashboard.blocks.bookings.title")}</CardTitle>
          {successfulBookings > 0 ? (
            <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
              {stats.successful_bookings_trend !== null &&
              stats.successful_bookings_trend !== undefined ? (
                <TrendIndicator
                  trend={stats.successful_bookings_trend}
                  currentValue={successfulBookings}
                  previousValue={previousSuccessfulBookings}
                  startDate={start}
                  endDate={end}
                  comparisonMode={comparisonMode}
                  formatValue={(value) =>
                    integerNumberFormatter.format(Math.round(value))
                  }
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col justify-end pb-6">
        {chartData.length > 0 ? (
          <>
            <div className="flex gap-6">
              <ChartContainer config={chartConfig} className="h-64 flex-1">
                <PieChart>
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Pie data={chartData} dataKey="count" nameKey="status" />
                </PieChart>
              </ChartContainer>

              <div className="flex flex-col justify-center gap-3">
                {statusMetrics
                  .filter((item) => item.count > 0)
                  .map((item) => (
                    <Link
                      key={item.key}
                      to="/$tenant/$orgId/supplier/{-$supplierId}/bookings"
                      params={(prev) => prev}
                      search={item.search}
                      className="flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-75"
                    >
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      />
                      <div className="text-sm">
                        <div className="font-medium">{item.status}</div>
                        <div className="text-gray-500">
                          {item.count} (
                          {totalBookings > 0
                            ? Math.round((item.count / totalBookings) * 100)
                            : 0}
                          %)
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>

            {stats.avg_confirmation_hours !== null &&
            stats.avg_confirmation_hours !== undefined ? (
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700">
                      {t("dashboard.blocks.bookings.avgConfirmationTime")}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InformationCircleIcon className="h-4 w-4 cursor-help text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          {t(
                            "dashboard.blocks.bookings.avgConfirmationTooltip",
                          )}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {stats.avg_confirmation_hours.toFixed(1)}h
                  </span>
                </div>
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500">
                  <div
                    className="absolute top-0 h-4 w-1 -translate-x-1/2 rounded-full bg-white shadow-md ring-2 ring-gray-900"
                    style={{
                      left: `${getConfirmationTimePosition(stats.avg_confirmation_hours)}%`,
                    }}
                  />
                </div>
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                  <span>{t("dashboard.blocks.bookings.confirmationGood")}</span>
                  <span>{t("dashboard.blocks.bookings.confirmationPoor")}</span>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-48 opacity-80">
              <IllustrationListApp />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              {t("dashboard.blocks.bookings.noData")}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {t("dashboard.blocks.bookings.noDataDescription")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
